import React from 'react';
import { Button } from 'react-bootstrap';
import { isTripBooking } from '../../Utils/bookingType';
import { EMAIL, isEmailChannel, WHATSAPP } from '../../Utils/messageChannel';

/**
 * The one control that offers to message a booking's PASSENGER, wherever the
 * dashboard shows a way of reaching them — their phone number, or their email
 * address.
 *
 * ONE COMPONENT, EVERY SITE, AND NOW EVERY CHANNEL, because "wherever the
 * contact details are shown" is otherwise an invitation to hand-roll the same
 * button four times and have them drift. It renders only the trigger: the
 * composer is hosted ONCE per screen and driven by which row was tapped,
 * because a table of thirty bookings must not mount thirty modals.
 *
 * `channel` PICKS THE GATE, not just the glyph — which is the whole reason it
 * is a prop here rather than two components. A WhatsApp trigger needs a phone
 * number; an email trigger needs an email address; and a booking very often
 * has one and not the other. Two components would have meant two copies of
 * the booking-type gate below, and the second copy is the one that gets
 * forgotten.
 *
 * IT GATES ON THE DATA PATH, NEVER ON APPEARANCE. In the Orders table the
 * passenger cell and the driver cell are the same markup to the character —
 * a bold name, then `small.text-muted.d-block` with a phone icon — and the
 * only thing that tells them apart is whether the number came from
 * `order.phone_number` or `order.driver.phone_number`. So this takes the
 * BOOKING and reads the passenger's own number or address itself. Anything
 * that matched on the look of a cell would put "message the passenger" on the
 * driver's number, and the office would send a passenger's instructions to
 * the driver. The same holds for the address: an operator's or a partner's
 * email is a SUPPLIER'S, it is never `order.email_address`, and nothing here
 * can reach one.
 *
 * AND IT GATES ON THE BOOKING BEING A JOURNEY. On an expense booking
 * `full_name` is the DESCRIPTION, not a person ("Fuel receipt March") —
 * `Utils/bookingType` spells this out, and the API refuses
 * `send_notification_to_customer` on one outright. There is nobody to write
 * to, so there is no control. `isTripBooking` is the existing name for that
 * question and is deliberately reused rather than restated as a
 * `booking_type !== 'expense'` test.
 *
 * Nothing at all when there is no way to reach them on this channel: never a
 * control that goes nowhere.
 */
export default function PassengerMessageButton({
  order, onOpen, channel = WHATSAPP, className = '',
}) {
  const email = isEmailChannel(channel);
  // The passenger's OWN number or address, off the booking — see IT GATES ON
  // THE DATA PATH above. Not a prop, so no call site can pass the wrong one.
  const reachable = email ? order?.email_address : order?.phone_number;

  if (!reachable || !isTripBooking(order)) return null;

  const who = order?.full_name ? ` ${order.full_name}` : ' the passenger';

  return (
    <Button
      variant="link"
      size="sm"
      className={`p-0 ${className}`.trim()}
      onClick={() => onOpen(order, email ? EMAIL : WHATSAPP)}
      title={email
        ? 'Email the passenger from the company address'
        : 'Message the passenger on WhatsApp, from the company number'}
      // Each channel's name is DISTINCT, and deliberately so: both triggers
      // can sit in one card, and "Message Heather Sanford" twice would leave
      // a screen-reader user with two identical controls doing two things.
      aria-label={email ? `Email${who}` : `Message${who} on WhatsApp`}
    >
      <i className={email ? 'fa fa-envelope text-primary' : 'fab fa-whatsapp text-success'} />
    </Button>
  );
}
